import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import jsPDF from "jspdf";
import "jspdf-autotable";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorPrescription.css";

const DoctorPrescription = () => {
  const { appointmentId } = useParams();
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [existingPrescription, setExistingPrescription] = useState(null);
  const [prescriptionData, setPrescriptionData] = useState({
    medications: [{ name: "", dosage: "", frequency: "", duration: "" }],
    instructions: "",
    nextAppointmentDate: "",
    nextAppointmentTime: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  useEffect(() => {
    const fetchData = async () => {
      if (!doctorUser || doctorUser.role !== "doctor") {
        setError("You must be logged in as a doctor to create a prescription.");
        setIsLoading(false);
        return;
      }

      if (!appointmentId || !/^[0-9a-fA-F]{24}$/.test(appointmentId)) {
        setError("Invalid appointment ID format.");
        setIsLoading(false);
        return;
      }

      try {
        const [doctorResponse, appointmentResponse, prescriptionResponse] = await Promise.all([
          api.get("/doctors/profile", { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }),
          api.get(`/appointments/${appointmentId}`, { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }),
          api.get(`/appointments/prescriptions/appointment/${appointmentId}`, { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } }).catch((err) => {
            if (err.response?.status === 404) return null; // Prescription not found is okay
            throw err;
          }),
        ]);

        setDoctor(doctorResponse.data);
        setAppointment(appointmentResponse.data);
        if (prescriptionResponse?.data) {
          setExistingPrescription(prescriptionResponse.data);
          setPrescriptionData({
            medications: prescriptionResponse.data.medications || [{ name: "", dosage: "", frequency: "", duration: "" }],
            instructions: prescriptionResponse.data.instructions || "",
            nextAppointmentDate: prescriptionResponse.data.nextAppointmentDate
              ? new Date(prescriptionResponse.data.nextAppointmentDate).toISOString().split('T')[0]
              : "",
            nextAppointmentTime: prescriptionResponse.data.nextAppointmentDate
              ? new Date(prescriptionResponse.data.nextAppointmentDate).toTimeString().slice(0, 5)
              : "",
          });
        }
      } catch (error) {
        setError("Failed to load prescription data: " + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [appointmentId, doctorUser]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAddMedication = () => {
    setPrescriptionData((prev) => ({
      ...prev,
      medications: [...prev.medications, { name: "", dosage: "", frequency: "", duration: "" }],
    }));
  };

  const handleMedicationChange = (index, field, value) => {
    const updatedMedications = [...prescriptionData.medications];
    updatedMedications[index][field] = value;
    setPrescriptionData((prev) => ({ ...prev, medications: updatedMedications }));
  };

  const handleSubmitPrescription = async () => {
    if (!doctorUser || isProcessing) return;
    setIsProcessing(true);
    setError(null); // Clear previous errors

    try {
      const validMedications = prescriptionData.medications.filter(
        (med) => med.name && med.dosage && med.frequency && med.duration
      );
      if (validMedications.length === 0) {
        throw new Error("At least one complete medication is required.");
      }

      const patientId = typeof appointment?.patientId === 'object'
        ? appointment?.patientId?._id?.toString()
        : appointment?.patientId?.toString();

      if (!patientId || !/^[0-9a-fA-F]{24}$/.test(patientId)) {
        throw new Error("Invalid patientId format.");
      }

      const doc = new jsPDF();
      doc.setFillColor(0, 102, 204);
      doc.rect(0, 0, 210, 30, "F");
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text("TureMedicine Prescription", 20, 20);
      doc.setFontSize(10);
      doc.text("Your Trusted Telehealth Platform", 20, 27);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Doctor Details", 20, 40);
      doc.setFont("helvetica", "normal");
      doc.text(`Name: ${doctor.name}`, 20, 47);
      doc.text(`Specialization: ${doctor.specialization}`, 20, 54);
      doc.text(`Contact: ${doctor.email}`, 20, 61);
      doc.setFont("helvetica", "bold");
      doc.text("Patient Details", 110, 40);
      doc.setFont("helvetica", "normal");
      doc.text(`Name: ${appointment.name}`, 110, 47);
      doc.text(`Age: ${appointment.age}, Gender: ${appointment.gender}`, 110, 54);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 110, 61);
      doc.setFont("helvetica", "bold");
      doc.text("Medications", 20, 75);
      doc.autoTable({
        startY: 80,
        head: [["Medication", "Dosage", "Frequency", "Duration"]],
        body: validMedications.map((m) => [m.name, m.dosage, m.frequency, m.duration]),
        theme: "grid",
        headStyles: { fillColor: [0, 102, 204], textColor: [255, 255, 255], fontSize: 12 },
        bodyStyles: { fontSize: 10 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 20, right: 20 },
      });

      let finalY = doc.lastAutoTable.finalY || 80;
      doc.setFont("helvetica", "bold");
      doc.text("Instructions", 20, finalY + 10);
      doc.setFont("helvetica", "normal");
      const instructionsSplit = doc.splitTextToSize(prescriptionData.instructions || "N/A", 170);
      doc.text(instructionsSplit, 20, finalY + 17);

      if (prescriptionData.nextAppointmentDate && prescriptionData.nextAppointmentTime) {
        const nextDateTime = `${prescriptionData.nextAppointmentDate} at ${prescriptionData.nextAppointmentTime}`;
        doc.setFont("helvetica", "bold");
        doc.text("Next Appointment", 20, finalY + 30);
        doc.setFont("helvetica", "normal");
        doc.text(nextDateTime, 20, finalY + 37);
      }

      const pdfBlob = doc.output("blob");
      const formData = new FormData();
      formData.append("appointmentId", appointmentId);
      formData.append("patientId", patientId);
      formData.append("doctorId", doctorUser.id);
      formData.append("medications", JSON.stringify(validMedications));
      formData.append("instructions", prescriptionData.instructions);
      if (prescriptionData.nextAppointmentDate && prescriptionData.nextAppointmentTime) {
        const nextDateTime = `${prescriptionData.nextAppointmentDate}T${prescriptionData.nextAppointmentTime}:00`;
        formData.append("nextAppointmentDate", nextDateTime);
      }
      formData.append("pdf", pdfBlob, "prescription.pdf");

      await api.post("/appointments/prescriptions", formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${doctorUser.accessToken}` },
      });

      await api.put(`/appointments/${appointmentId}/prescription-complete`, {}, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });

      navigate("/prescription-success?appointmentId=" + appointmentId);
    } catch (error) {
      setError("Failed to submit prescription: " + (error.response?.data?.message || error.message));
      console.error("Prescription submission error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (isLoading) return (
    <div className="doctor-prescription-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="prescription-main-content">
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <div className="prescription-loading">Loading prescription data...</div>
      </div>
    </div>
  );

  if (error) return (
    <div className="doctor-prescription-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="prescription-main-content">
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <div className="prescription-error">
          {error}
          <button onClick={() => { setError(null); navigate("/doctor-login"); }}>Go to Login</button>
          <button onClick={() => { setError(null); navigate("/doctor-dashboard"); }}>Back to Dashboard</button>
        </div>
      </div>
    </div>
  );

  if (!doctor || !appointment) return (
    <div className="doctor-prescription-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="prescription-main-content">
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <div className="prescription-error">No data available.</div>
      </div>
    </div>
  );

  return (
    <div className="doctor-prescription-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="prescription-main-content">
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <h2 className="prescription-title">{existingPrescription ? "Update Prescription" : "Create Prescription"}</h2>
        {existingPrescription && (
          <p className="prescription-notice">
            Prescription created on: {new Date(existingPrescription.createdAt).toLocaleString()}
          </p>
        )}
        {error && <div className="prescription-error">{error}</div>}
        <div className="prescription-platform-info">
          <p><strong>TureMedicine:</strong> Your trusted telehealth platform.</p>
          <p><strong>Website:</strong> www.turemedicine.com | <strong>Contact:</strong> support@turemedicine.com</p>
        </div>
        <div className="prescription-info-container">
          <div className="prescription-doctor-info">
            <h3>Doctor Details</h3>
            <p><strong>Name:</strong> {doctor.name}</p>
            <p><strong>Specialization:</strong> {doctor.specialization}</p>
            <p><strong>Contact:</strong> {doctor.email}</p>
          </div>
          <div className="prescription-patient-info">
            <h3>Patient Details</h3>
            <p><strong>Name:</strong> {appointment.name}</p>
            <p><strong>Age:</strong> {appointment.age}</p>
            <p><strong>Gender:</strong> {appointment.gender}</p>
            <p><strong>Last Appointment:</strong> {new Date(appointment.date).toLocaleString()}</p>
          </div>
        </div>

        <div className="prescription-form-container">
          <h3 className="prescription-form-title">Prescription Details</h3>
          {prescriptionData.medications.map((med, index) => (
            <div key={index} className="medication-input-group">
              <input
                type="text"
                placeholder="Medicine Name"
                value={med.name}
                onChange={(e) => handleMedicationChange(index, "name", e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Dosage (e.g., 500mg)"
                value={med.dosage}
                onChange={(e) => handleMedicationChange(index, "dosage", e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Frequency (e.g., Twice daily)"
                value={med.frequency}
                onChange={(e) => handleMedicationChange(index, "frequency", e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Duration (e.g., 7 days)"
                value={med.duration}
                onChange={(e) => handleMedicationChange(index, "duration", e.target.value)}
                required
              />
            </div>
          ))}
          <button className="add-medication-button" onClick={handleAddMedication}>
            Add Another Medication
          </button>

          <div className="prescription-instructions">
            <label>Instructions:</label>
            <textarea
              value={prescriptionData.instructions}
              onChange={(e) => setPrescriptionData({ ...prescriptionData, instructions: e.target.value })}
              rows="4"
              placeholder="Additional instructions..."
            />
          </div>

          <div className="prescription-next-appointment">
            <h4>Next Appointment (Optional)</h4>
            <div className="appointment-date-time">
              <div>
                <label>Date:</label>
                <input
                  type="date"
                  value={prescriptionData.nextAppointmentDate}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, nextAppointmentDate: e.target.value })}
                />
              </div>
              <div>
                <label>Time:</label>
                <input
                  type="time"
                  value={prescriptionData.nextAppointmentTime}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, nextAppointmentTime: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="prescription-actions">
            <button
              className="submit-prescription-button"
              onClick={handleSubmitPrescription}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : (existingPrescription ? "Update & Send PDF" : "Generate & Send PDF")}
            </button>
            <button className="cancel-prescription-button" onClick={() => navigate("/doctor/patients")}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorPrescription;